import { Toaster } from '@/components/ui/toaster';
import { RouteErrorBoundary } from '@/components/PageErrorBoundary';
import { AppLayout } from '@/layouts/AppLayout';
import { api } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { useThemeStore } from '@/store/themeStore';
import { UserRole } from '@shared/types';
import { Loader2 } from 'lucide-react';
import { lazy,useCallback,useEffect,useState } from 'react';
import type { ComponentType } from 'react';
import { Navigate,Outlet,Route,Routes } from 'react-router';

// Eagerly loaded (login is the entry point)
import Login from '@/pages/Login';

// Lazy-loaded route pages
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const InventoryList = lazy(() => import('@/pages/Inventory/InventoryList'));
const InventoryDetail = lazy(() => import('@/pages/Inventory/InventoryDetail'));
const InventoryEdit = lazy(() => import('@/pages/Inventory/InventoryEdit'));
const InventoryImport = lazy(() => import('@/pages/Inventory/InventoryImport'));
const InventoryImportDetail = lazy(() => import('@/pages/Inventory/InventoryImportDetail'));
const CycleCounts = lazy(() => import('@/pages/Inventory/CycleCounts'));
const CycleCountDetail = lazy(() => import('@/pages/Inventory/CycleCountDetail'));
const SkuMappingDashboard = lazy(() => import('@/pages/Inventory/SkuMappingDashboard'));
const DataEnrichment = lazy(() => import('@/pages/Inventory/DataEnrichment'));
const Warehouses = lazy(() => import('@/pages/Inventory/Warehouses'));
const Transfers = lazy(() => import('@/pages/Inventory/Transfers'));
const LotManagement = lazy(() => import('@/pages/Inventory/LotManagement'));
const SerialManagement = lazy(() => import('@/pages/Inventory/SerialManagement'));
const RecipeList = lazy(() => import('@/pages/Recipes/RecipeList'));
const RecipeDetail = lazy(() => import('@/pages/Recipes/RecipeDetail'));
const RecipeEdit = lazy(() => import('@/pages/Recipes/RecipeEdit'));
const ProduceWizard = lazy(() => import('@/pages/Produce/ProduceWizard'));
const SupplyChainOverview = lazy(() => import('@/pages/SupplyChain/SupplyChainOverview'));
const VendorManagement = lazy(() => import('@/pages/SupplyChain/VendorManagement'));
const VendorIntelligence = lazy(() => import('@/pages/SupplyChain/VendorIntelligence'));
const QualityControl = lazy(() => import('@/pages/QualityControl'));
const Reports = lazy(() => import('@/pages/Reports'));
const AnomalyInbox = lazy(() => import('@/pages/Reports/AnomalyInbox'));
const AnomalyDashboard = lazy(() => import('@/pages/Reports/AnomalyDashboard'));
const Settings = lazy(() => import('@/pages/Settings'));
const QuickBooksIntegration = lazy(() => import('@/pages/Settings/QuickBooksIntegration'));
const Integrations = lazy(() => import('@/pages/Settings/Integrations'));
const SystemJobs = lazy(() => import('@/pages/System/Jobs'));
const StockBehaviorMigration = lazy(() => import('@/pages/System/StockBehaviorMigration'));
const ComplianceCenter = lazy(() => import('@/pages/System/Compliance'));
const Diagnostics = lazy(() => import('@/pages/System/Diagnostics'));
const WebhookManagement = lazy(() => import('@/pages/System/Webhooks'));
const Admin = lazy(() => import('@/pages/System/Admin'));
const Shipping = lazy(() => import('@/pages/Shipping'));
const DamageClaimsInbox = lazy(() => import('@/pages/Shipping/DamageClaimsInbox'));
const OrderBoard = lazy(() => import('@/pages/Orders/OrderBoard'));
const ScanVision = lazy(() => import('@/pages/Scan/ScanVision'));
const ReferenceImageManager = lazy(() => import('@/pages/Scan/ReferenceImageManager'));
const GanttSchedule = lazy(() => import('@/pages/Scheduling/GanttChart'));
const ManufacturingOrders = lazy(() => import('@/pages/Manufacturing/ManufacturingOrders'));
const ManufacturingOrderDetail = lazy(() => import('@/pages/Manufacturing/ManufacturingOrderDetail'));
const WorkOrderRunDetail = lazy(() => import('@/pages/Manufacturing/WorkOrderRunDetail'));
const LaborTracking = lazy(() => import('@/pages/Labor/LaborTracking'));
const ApprovalCenter = lazy(() => import('@/pages/Approvals/ApprovalCenter'));
const OnboardingWizard = lazy(() => import('@/pages/Onboarding/OnboardingWizard'));
const VerifyEmail = lazy(() => import('@/pages/VerifyEmail'));
const VerifyEmailPending = lazy(() => import('@/pages/VerifyEmailPending'));
const MagicLinkVerify = lazy(() => import('@/pages/MagicLinkVerify'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const PublicDamageReport = lazy(() => import('@/pages/PublicDamageReport'));
const KitList = lazy(() => import('@/pages/Kits/KitList'));
const KitDetail = lazy(() => import('@/pages/Kits/KitDetail'));
const QuoteList = lazy(() => import('@/pages/Quotes/QuoteList'));
const CustomFields = lazy(() => import('@/pages/Settings/CustomFields'));
const RMAList = lazy(() => import('@/pages/RMA/RMAList'));
const RMADetail = lazy(() => import('@/pages/RMA/RMADetail'));
const PackingListPage = lazy(() => import('@/pages/Packing/PackingList'));
const PackingDetail = lazy(() => import('@/pages/Packing/PackingDetail'));
const PackStation = lazy(() => import('@/pages/Packing/PackStation'));
const SubcontractList = lazy(() => import('@/pages/Subcontracting/SubcontractList'));
const SubcontractDetail = lazy(() => import('@/pages/Subcontracting/SubcontractDetail'));
const CapacityDashboard = lazy(() => import('@/pages/Capacity/CapacityDashboard'));
const ResourceList = lazy(() => import('@/pages/Capacity/ResourceList'));
const ResourceDetail = lazy(() => import('@/pages/Capacity/ResourceDetail'));
const MPSDashboard = lazy(() => import('@/pages/MPS'));
const PlanDetail = lazy(() => import('@/pages/MPS/PlanDetail'));
const DemandForecast = lazy(() => import('@/pages/MPS/DemandForecast'));

type InventoryAssistantComponent = ComponentType<{ defaultOpen?: boolean }>;

function RequireAuth() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const accessToken = useAuthStore((s) => s.accessToken);
  const [restoreAttempted, setRestoreAttempted] = useState(false);

  // Fresh tabs may have only the HttpOnly refresh cookie. Restore does not
  // depend on localStorage because browsers/users may clear persisted hints.
  const needsRestore = !accessToken && !restoreAttempted;
  useEffect(() => {
    if (needsRestore) {
      void api.restoreSession().finally(() => setRestoreAttempted(true));
    }
  }, [needsRestore]);

  if (needsRestore) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!isAuthenticated || !accessToken) {
    return <Navigate to="/login" replace />;
  }
  return <Outlet />;
}

function RequireVerified() {
  const user = useAuthStore((s) => s.user);
  if (user && user.emailVerified === false) {
    return <Navigate to="/verify-email-pending" replace />;
  }
  return <Outlet />;
}

function RequireOnboarding() {
  const user = useAuthStore((s) => s.user);
  if (user && user.onboardingCompleted === false) {
    return <Navigate to="/onboarding" replace />;
  }
  return <Outlet />;
}

function AccessDenied() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center px-6" data-testid="access-denied-page">
      <div className="max-w-md text-center">
        <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Access denied</p>
        <h1 className="mt-3 text-3xl font-bold tracking-tight">Forbidden</h1>
        <p className="mt-3 text-muted-foreground">
          Your role cannot access this page.
        </p>
      </div>
    </div>
  );
}

function RequireRole({ allowedRoles }: { allowedRoles: UserRole[] }) {
  const role = useAuthStore((s) => s.user?.role);
  if (!role || !allowedRoles.includes(role)) {
    return <AccessDenied />;
  }
  return <Outlet />;
}

function InventoryAssistantLoader() {
  const [Assistant, setAssistant] = useState<InventoryAssistantComponent | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleOpen = useCallback(() => {
    if (Assistant || isLoading) return;
    setIsLoading(true);
    void import('@/components/chat/InventoryAssistant')
      .then((module) => {
        setAssistant(() => module.InventoryAssistant);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [Assistant, isLoading]);

  if (Assistant) {
    return <Assistant defaultOpen />;
  }

  return (
    <button
      onClick={handleOpen}
      data-testid="inventory-assistant-toggle"
      className="fixed bottom-6 right-6 z-50 flex h-12 w-12 items-center justify-center border border-primary/80 bg-primary text-primary-foreground transition-all hover:brightness-110 disabled:cursor-wait disabled:opacity-70"
      style={{ borderRadius: 0 }}
      title="Inventory Assistant"
      disabled={isLoading}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    </button>
  );
}

function App() {
  const { theme, highContrast } = useThemeStore();

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark', 'oled');

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      root.classList.add(systemTheme);
    } else {
      root.classList.add(theme);
    }

    if (highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }
  }, [theme, highContrast]);

  return (
    <>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/auth/magic-link" element={<MagicLinkVerify />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/damage-report/:token" element={<PublicDamageReport />} />
        <Route element={<RequireAuth />}>
          <Route element={<RouteErrorBoundary />}>
            <Route path="/verify-email-pending" element={<VerifyEmailPending />} />
            <Route path="/onboarding" element={<OnboardingWizard />} />
          </Route>
          <Route element={<RequireVerified />}>
          <Route element={<RequireOnboarding />}>
          <Route element={<AppLayout />}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/inventory" element={<InventoryList />} />
            <Route element={<RequireRole allowedRoles={[UserRole.ADMIN, UserRole.MANAGER]} />}>
              <Route path="/inventory/import" element={<InventoryImport />} />
              <Route path="/inventory/import/:jobId" element={<InventoryImportDetail />} />
            </Route>
            <Route path="/inventory/sku-mapping" element={<SkuMappingDashboard />} />
            <Route path="/inventory/data-enrichment" element={<DataEnrichment />} />
            <Route path="/inventory/warehouses" element={<Warehouses />} />
            <Route path="/inventory/transfers" element={<Transfers />} />
            <Route path="/inventory/lots" element={<LotManagement />} />
            <Route path="/inventory/serials" element={<SerialManagement />} />
            <Route path="/inventory/cycle-counts" element={<CycleCounts />} />
            <Route path="/inventory/cycle-counts/:id" element={<CycleCountDetail />} />
            <Route path="/inventory/:id" element={<InventoryDetail />} />
            <Route path="/inventory/:id/edit" element={<InventoryEdit />} />
            <Route path="/recipes" element={<RecipeList />} />
            <Route path="/recipes/new" element={<RecipeEdit />} />
            <Route path="/recipes/:id" element={<RecipeDetail />} />
            <Route path="/recipes/:id/edit" element={<RecipeEdit />} />
            <Route path="/produce" element={<ProduceWizard />} />
            <Route path="/scheduling" element={<GanttSchedule />} />
            <Route path="/manufacturing" element={<ManufacturingOrders />} />
            <Route path="/manufacturing/runs/:id" element={<WorkOrderRunDetail />} />
            <Route path="/manufacturing/:id" element={<ManufacturingOrderDetail />} />
            <Route path="/labor" element={<LaborTracking />} />
            <Route path="/approvals" element={<ApprovalCenter />} />
            <Route path="/supply-chain" element={<SupplyChainOverview />} />
            <Route path="/supply-chain/vendors" element={<VendorManagement />} />
            <Route path="/supply-chain/intelligence" element={<VendorIntelligence />} />
            <Route path="/quality-control" element={<QualityControl />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/reports/anomalies" element={<AnomalyInbox />} />
            <Route path="/reports/anomaly-dashboard" element={<AnomalyDashboard />} />
            <Route path="/settings" element={<Settings />} />
            <Route element={<RequireRole allowedRoles={[UserRole.ADMIN, UserRole.MANAGER]} />}>
              <Route path="/settings/quickbooks" element={<QuickBooksIntegration />} />
              <Route path="/settings/integrations" element={<Integrations />} />
              <Route path="/system/stock-behavior-migration" element={<StockBehaviorMigration />} />
            </Route>
            <Route element={<RequireRole allowedRoles={[UserRole.ADMIN]} />}>
              <Route path="/system/jobs" element={<SystemJobs />} />
              <Route path="/system/admin" element={<Admin />} />
              <Route path="/system/compliance" element={<ComplianceCenter />} />
              <Route path="/system/diagnostics" element={<Diagnostics />} />
              <Route path="/system/webhooks" element={<WebhookManagement />} />
            </Route>
            <Route path="/shipping" element={<Shipping />} />
            <Route path="/shipping/damage-claims" element={<DamageClaimsInbox />} />
            <Route path="/orders/board" element={<OrderBoard />} />
            <Route path="/packing" element={<PackingListPage />} />
            <Route path="/packing/:id" element={<PackingDetail />} />
            <Route path="/packing/:id/pack-station" element={<PackStation />} />
            <Route path="/kits" element={<KitList />} />
            <Route path="/kits/:id" element={<KitDetail />} />
            <Route path="/quotes" element={<QuoteList />} />
            <Route path="/settings/custom-fields" element={<CustomFields />} />
            <Route path="/rma" element={<RMAList />} />
            <Route path="/rma/:id" element={<RMADetail />} />
            <Route path="/subcontracting" element={<SubcontractList />} />
            <Route path="/subcontracting/:id" element={<SubcontractDetail />} />
            <Route path="/capacity" element={<CapacityDashboard />} />
            <Route path="/capacity/resources" element={<ResourceList />} />
            <Route path="/capacity/resources/:id" element={<ResourceDetail />} />
            <Route path="/mps" element={<MPSDashboard />} />
            <Route path="/mps/:id" element={<PlanDetail />} />
            <Route path="/mps/demand" element={<DemandForecast />} />
            <Route path="/scan" element={<ScanVision />} />
            <Route path="/reference-images" element={<ReferenceImageManager />} />
          </Route>
          </Route>
          </Route>
        </Route>
      </Routes>
      <InventoryAssistantLoader />
      <Toaster />
    </>
  );
}

export default App;
